import {AccessCodeAlphabet, Occupancy, OccupyRequest} from "@variocube/app-api";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {auditLog} from "./auditLog";
import {CodeView} from "./CodeView";
import {Case, Cases, Plans} from "./model";
import {StorageView} from "./StorageView";
import {SuccessView} from "./SuccessView";
import {BoxTypeSelectionResult, usePlatform, useStorageItemValue} from "@variocube/cube-ui";
import {useLocalization} from "../i18n";
import {BoxSelectionView} from "./BoxSelectionView";

interface DispatchFlowProps {
    sizes: string[];
    onExit: () => void;
}

export function DispatchFlow({onExit, sizes}: DispatchFlowProps) {
    const platform = usePlatform();
    const {t} = useLocalization();
    const cases = useStorageItemValue<Cases>("cases");
    const plans = useStorageItemValue<Plans>("plans");


    const [caseData, setCaseData] = useState<Case>();
    const [boxSelection, setBoxSelection] = useState<BoxTypeSelectionResult>();
    const [boxNumber, setBoxNumber] = useState<string>();
    const [success, setSuccess] = useState(false);

    const handleVerify = useCallback(async (code:string) => {
        const caseData = (cases || {})[code];
        if (caseData) {
            if (caseData.transfer || caseData.dispatch) {
                setCaseData(caseData);
            }
            else {
                console.error('Invalid case found for entered code', caseData);
                throw new Error('Invalid case.')
            }
        }
        else {
            throw new Error('Invalid code.');
        }
    }, [cases]);

    const handleSizeBack = useCallback(() => setCaseData(undefined), []);

    const request = useMemo((): OccupyRequest|undefined => {
        if ((boxSelection || boxNumber) && caseData && plans) {
            const pickupCode = getCasePickupCode(caseData, plans);
            const access = pickupCode ? { accessCode: pickupCode } : {
                accessCodeShape: {
                    alphabet: AccessCodeAlphabet.OneToNine,
                    length: 6,
                }
            }
            return {
                ...boxSelection,
                boxNumber,
                ...access,
                content: {
                    dispatch: caseData.dispatch,
                    transfer: caseData.transfer
                },
            };
        }
    }, [caseData, boxNumber, boxSelection, plans]);

    const handleWrongSize = useCallback(() => setBoxSelection(undefined), []);

    const handleStorageConfirm = useCallback(async (occupancy: Occupancy) => {
        await auditLog(platform, 'Store', {
            occupancy,
            "case": caseData
        });
        setSuccess(true);
    }, [platform, caseData]);

    const storageStrings = useMemo(() => {
        if (caseData?.transfer) {
            return {
                boxOpenTitle: "TransferStoreTitle",
                boxOpenMessage: "TransferStoreMessage",
                confirmTitle: "TransferConfirmTitle",
                confirmMessage: "TransferConfirmMessage",
            } as const;
        }
        else {
            return {
                boxOpenTitle: "DispatchStoreTitle",
                boxOpenMessage: "DispatchStoreMessage",
                confirmTitle: "DispatchConfirmTitle",
                confirmMessage: "DispatchConfirmMessage",
            } as const;
        }
    }, [caseData]);

    const successStrings = useMemo(() => {
        if (caseData) {
            if (caseData.transfer) {
                return {
                    title: t("TransferSuccessTitle"),
                    message: t("TransferSuccessMessage")
                };
            }
            else if (caseData.dispatch) {
                return {
                    title: t("DispatchSuccessTitle"),
                    message: t("DispatchSuccessMessage")
                };
            }
        }
        return {
            title: "",
            message: "",
        }
    }, [caseData]);

    if (!caseData) {
        return (
            <CodeView
                onBack={onExit}
                title={t("DispatchCodeTitle")}
                description={t("DispatchCodeDescription")}
                onVerify={handleVerify} />
        )
    }

    if (!boxSelection && !boxNumber) {
        return (
            <BoxSelectionView
                sizes={sizes}
                onTypeSelect={setBoxSelection}
                onNumberSelect={setBoxNumber}
                onBack={handleSizeBack}
            />
        );
    }

    if (!success) {
        if (!request) {
            console.error("Invalid state. request not defined.");
            return null;
        }
        return (
            <StorageView
                request={request}
                onWrongSize={handleWrongSize}
                onCancel={onExit}
                onConfirm={handleStorageConfirm}
                {...storageStrings}
            />
        );
    }

    return (
        <SuccessView
            onClose={onExit}
            {...successStrings}
        />
    )
}

function getCasePickupCode(caseData: Case, plans: Plans) {
    return caseData.pickupCode || getDispatchPlanPickupCode(caseData, plans);
}

function getDispatchPlanPickupCode(caseData: Case, plans: Plans) {
    if (caseData.dispatch && caseData.dispatch.planId) {
        const plan = plans[caseData.dispatch.planId];
        if (plan && plan.dispatchPickupCode) {
            return plan.dispatchPickupCode;
        }
    }
    return undefined;
}